import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchComparisionPrompts,
  promptsSelector,
  removePromptsComparision,
} from "../../../../store/slices/prompts";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { companiesSelector } from "../../../../store/slices/company";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { convertArrayToDictionary } from "../../../../common/utils";
import _, { set, size } from "lodash";
import moment from "moment";
import PromptCompare from "./promptCompare";
import { locationsSelector } from "../../../../store/slices/location";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';


// Define keys that should be excluded from being displayed red in the table
const excludedKeys = [
  "modifiedBy",
  "modifiedOn",
  "createdBy",
  "createdOn",
  "companyId",
  "sysEndTime",
  "sysStartTime",
  "promptId",
  "checkbox",
  "defaultParameters",
];

// Component for displaying the history of a prompt
const HistoryPromptDetails = ({ open = false, handleClose, promptId }) => {
  // Redux state selectors
  const { historyPromptDetails } = useSelector(promptsSelector);
  const { users } = useSelector(locationsSelector);

  // MUI theme
  const theme = useTheme();

  // Local state for selected data and comparison modal
  const [selectedData, setSelectedData] = useState([]);
  const [openComparison, setOpenComparison] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [comparisonType, setComparisonType] = useState("promptText");

  // Memoized dictionary of users
  const userDic = React.useMemo(
    () =>
      convertArrayToDictionary(
        _.map(_.cloneDeep(users), (it) => {
          it.name = it.firstName + (it.lastName ? " " + it.lastName : "");
          return it;
        }),
        "userId"
      ),
    [users]
  );

  // Memoized and processed data for the MaterialReactTable
  const data = useMemo(() => {
    if (!historyPromptDetails || historyPromptDetails.length === 0) {
      return [];
    }

    // Sort history data by sysStartTime in ascending order
    const sortedData = _.orderBy(historyPromptDetails, "sysStartTime", "asc");
    const tempData = sortedData.map((obj) => {
      const defaultParams = JSON.parse(obj.defaultParameters || "{}");
      const mergedObj = {
        ...obj,
        ...defaultParams,
        modifiedBy: userDic[obj?.modifiedBy]?.name,
      };
      return mergedObj;
    });
    // Process and merge data for display
    const result = tempData.map((obj, index, array) => {
      // const defaultParams = JSON.parse(obj.defaultParameters || "{}");
      // const mergedObj = {
      //   ...obj,
      //   ...defaultParams,
      //   modifiedBy: userDic[obj?.modifiedBy]?.name,
      // };
      const mergedObj = { ...obj };
      const changes = Object.keys(mergedObj).reduce((result, key) => {
        if (
          key !== "promptId" &&
          key !== "sysStartTime" &&
          key !== "sysEndTime" &&
          key !== "companyId" &&
          key !== "createdOn" &&
          key !== "createdBy" &&
          key !== "modifiedOn" &&
          key !== "modifiedBy" &&
          key !== "defaultParameters"
        ) {
          result[key] = {
            isChanged:
              index === 0 ? false : mergedObj[key] !== array[index - 1][key],
            value: mergedObj[key],
          };
        }
        return result;
      }, {});

      return {
        ...mergedObj,
        ...changes,
      };
    });

    // Sort the result by sysStartTime in descending order
    return _.orderBy(result, (obj) => obj.sysStartTime, "desc");
  }, [historyPromptDetails, userDic]);

  // Handle closing the dialog
  const onClose = () => {
    handleClose();
    setSelectedData([]);
  };

  // Handle selection of data in the table
  const handleSelectedData = (e, row, selectedData) => {
    if (e.target.checked) {
      if (_.size(selectedData) >= 2) {
        let data = [selectedData[0]];
        data.unshift(row.sysStartTime);
        setSelectedData(data);
      } else {
        setSelectedData((prev) => [...prev, row?.sysStartTime]);
      }
    } else {
      setSelectedData((prev) =>
        _.filter(prev, (it) => it !== row?.sysStartTime)
      );
    }
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  }
  // Handle opening the comparison modal
  const handleOpenComparisonModal = (comparisonType) => {
    if (promptId && _.size(selectedData) === 2) {
      setOpenComparison(true);
      handleCloseMenu();
      setComparisonType(comparisonType)
    }
  };

  // Handle closing the comparison modal
  const handleCloseComparisionModal = () => {
    setOpenComparison(false);
  };

  // Define columns for the MaterialReactTable
  const columns = useMemo(
    () => [
      {
        header: "",
        accessorKey: "",
        id: "checkbox",
        Cell: ({ row }) => {
          return (
            <Checkbox
              size="small"
              checked={_.includes(selectedData, row.original.sysStartTime)}
              onChange={(e) =>
                handleSelectedData(e, row?.original, selectedData)
              }
            />
          );
        },
      },
      {
        header: "Modified By",
        accessorKey: "modifiedBy",
        // Cell: ({ row }) => userDic[row?.original?.modifiedBy]?.name,
      },
      {
        header: "Modified On",
        accessorKey: "sysStartTime",
        Cell: ({ row }) =>
          moment
            .utc(row?.original?.sysStartTime)
            .local()
            .format("MM/DD/YYYY hh:mm A"),
      },
      {
        header: "System Prompt",
        accessorKey: "systemPrompt.value",
        id: "systemPrompt",
        size: 150,
        Cell: ({ row }) =>
          row?.original?.systemPrompt?.value?row?.original?.systemPrompt?.isChanged ? "<Changed>" : "<Unchanged>":null,
      },
      {
        header: "User Prompt",
        accessorKey: "promptText.value",
        id: "promptText",
        size: 120,
        Cell: ({ row }) =>
          row?.original?.promptText?.isChanged ? "<Changed>" : "<Unchanged>",
      },
      {
        header: "JSON Schema",
        accessorKey: "responseJsonSchema.value",
        id: "responseJsonSchema",
        size: 100,
        Cell: ({ row }) =>
          row?.original?.responseJsonSchema?.value?row?.original?.responseJsonSchema?.isChanged ? "<Changed>" : "<Unchanged>":null,
      },
      {
        header: "Name",
        accessorKey: "name.value",
        id: "name",
        Cell: ({ row }) =><Tooltip title={row?.original?.name?.value.length>38 ? row?.original?.name?.value : ""} arrow disableInteractive> <Typography sx={{width:'280px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{row?.original?.name?.value}</Typography></Tooltip>
      },
      {
        header: "Version",
        accessorKey: "version.value",
        id: "version",
        size: 100,
      },
      {
        header: "Type",
        accessorKey: "type.value",
        id: "type",
        size: 100,
      },
      
      {
        header: "Sub Type",
        accessorKey: "subType.value",
        id: "subType",
      },

      {
        header: "Review Selection Type",
        accessorKey: "reviewSelectionType.value",
        id: "reviewSelectionType",
      },
      {
        header: "API",
        accessorKey: "api.value",
        id: "api",
        size: 120,
      },
      {
        header: "Next Prompt",
        accessorKey: "nextPromptId.value",
        id: "nextPromptId",
        size: 120,
      },
      {
        header: "Description",
        accessorKey: "description.value",
        id: "description",
        size: 120,
        Cell: ({ row }) =>
          row?.original?.description?.isChanged ? "<Changed>" : "<Unchanged>",
      },
      
      {
        header: "LLM",
        accessorKey: "llm.value",
        id: "llm",
        size: 120,
      },
      {
        header: "Caching",
        accessorKey: "caching.value",
        id: "caching",
        size: 100,
        Cell: ({ row }) =>
          row?.original?.caching?.value ? "Yes" : "No",
      },
      
      {
        header: "Pass Reviews to AI",
        accessorKey: "processSingleReview.value",
        id: "processSingleReview",
        Cell: ({ row }) =>
          row?.original.reviewSelectionType?.value === "Single"
            ? null
            : row?.original.processSingleReview?.value
            ? "Yes"
            : "No",
      },
      
      
      {
        header: "Verbosity",
        accessorKey: "verbosity.value",
        id: "verbosity",
        size: 100,
      },
      {
        header: "Reasoning Effort",
        accessorKey: "reasoning_effort.value",
        id: "reasoning_effort",
        size: 150,
      },
      {
        header: "Service Tier",
        accessorKey: "serviceTier.value",
        id: "serviceTier",
        size: 150,
      },
      {
        header: "Temperature",
        accessorKey: "temperature.value",
        id: "temperature",
        size: 100,
      },
      {
        header: "TopP",
        accessorKey: "topP.value",
        id: "topP",
        size: 90,
      },
      {
        header: "Presence Penalty",
        accessorKey: "presencePenalty.value",
        id: "presencePenalty",
        size: 120,
      },
      {
        header: "Frequency Penalty",
        accessorKey: "frequencyPenalty.value",
        id: "frequencyPenalty",
        size: 120,
      },
      {
        header: "Max Input Length",
        accessorKey: "maxLength.value",
        id: "maxLength",
        size: 150,
      },
      {
        header: "Seed",
        accessorKey: "seed.value",
        id: "seed",
        size: 80,
        Cell: ({ row }) => (row.original.seed?.value),
      },
      // {
      //   header: "Input Factor",
      //   accessorKey: "inputFactor.value",
      //   id: "inputFactor",
      //   Cell: ({ row }) => row.original.inputFactor?.value?(row.original.inputFactor?.value + "%"):null,
      // },
      {
        header: "Json Response",
        accessorKey: "jsonResponse.value",
        id: "jsonResponse",
        size: 120,
        Cell: ({ row }) => (row.original.jsonResponse?.value ? "Yes" : "No"),
      },
      
      {
        header: "Website Research Tool",
        accessorKey: "web_search.value",
        id: "web_search",
        Cell: ({ row }) =>
          row?.original?.web_search?.value ? "Yes" : "No",
      },
      {
        header: "Response JSON Schema",
        accessorKey: "isJsonResponseSchema.value",
        id: "isJsonResponseSchema",
        size: 200,
        Cell: ({ row }) =>
          row?.original?.isJsonResponseSchema?.value ? "Yes" : "No",
      },
      
    ],
    [selectedData, data]
  );

  // Configuration for MaterialReactTable
  const table = useMaterialReactTable({
    data,
    columns,
    enableFullScreenToggle: false,
    enableStickyHeader: true,
    enablePagination: false,
    enableDensityToggle: false,
    enableColumnFilters: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableSorting: false,
    enableHiding: false,
    enableColumnActions: false,
    muiTableContainerProps: {
      sx: {
        maxHeight: "500px",
      },
    },
    muiTablePaperProps: {
      className: "std_table review-requests",
      sx: { boxShadow: "none" },
    },
    muiTableHeadProps: {
      sx: {
        position: "sticky",
        left: 0,
        top: 0,
      },
    },
    muiTableBodyCellProps: ({ row, column }) => {
      const cellValue = !_.includes(excludedKeys, column?.id)
        ? row.original[column.id]
        : null;
      if (column.id === "jsonResponse") {
        // console.log("cellValue", cellValue);
      }
      return {
        sx: {
          backgroundColor: cellValue
            ? cellValue?.isChanged
              ? `#EB888B !important`
              : "transparent"
            : "transparent",
          zIndex: 1,
          border: "none",
          padding: "4px 12px",
          "&:first-child": {
            minWidth: "50px !important",
            maxWidth: "50px !important",
          },
        },
      };
    },
    muiTableHeadCellProps: {
      sx: {
        padding: "4px 12px",
        "&:first-child": {
          minWidth: "50px !important",
          maxWidth: "50px !important",
        },
      },
    },
  });

  // Render the component
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth={"ultra"}>
        <DialogTitle>
          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Typography variant="h5">History</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <MaterialReactTable table={table} />
        </DialogContent>
        <DialogActions>
          <Stack
            direction="row"
            spacing={2}
            ml={2}
            py={1}
            sx={{ width: "100%" }}
          >
            {/* Button to open the comparison modal */}
            {/* <Button
              variant="contained"
              disabled={_.size(selectedData) < 2}
              onClick={handleOpenComparisonModal}
            >
              Compare
            </Button> */}

             {<Button
              variant="contained"
              disabled={_.size(selectedData) < 2}
              onClick={() => handleOpenComparisonModal(comparisonType)}
              
              
            >
              {'Compare'} 
              {<IconButton
                size="small"
                onClick={(e) => { setAnchorEl(e.currentTarget); e.stopPropagation(); }}
                sx={{ py:0 }}
              >
                <KeyboardArrowDownIcon sx={{color:'#fff',}}/>
              </IconButton>}
            </Button>}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
              // onClick={handleCompare}
            >
              <MenuItem value="systemPrompt" onClick={(e)=>{handleOpenComparisonModal("systemPrompt")}}>System Prompt</MenuItem>
              <MenuItem value="promptText" onClick={(e)=>{handleOpenComparisonModal("promptText")}}>User Prompt</MenuItem>
              <MenuItem value="responseJsonSchema" onClick={(e)=>{handleOpenComparisonModal("responseJsonSchema")}}>Response Json Schema</MenuItem>
            </Menu>
            {/* Button to cancel and close the dialog */}
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
      {/* Comparison modal */}
      {openComparison && (
        <PromptCompare
          open={openComparison}
          handleClose={handleCloseComparisionModal}
          selectedData={selectedData}
          comparisonType={comparisonType}
        />
      )}
    </>
  );
};

export default HistoryPromptDetails;
